/**
 * @file Loads the polyfills required for the app to work on all supported browsers.
 */

// TODO: CH-40136 - Write unit tests and remove the following line:
/* istanbul ignore file */

import isUndefined from "lodash/isUndefined";

/**
 * Helper used to log a message to the console when something gets polyfilled
 * @param {string} feature Name of the feature being polyfilled
 */
function logPolyfill(feature) {
  console.log("Polyfilling " + feature);
}

/**
 * Loads the polyfills for the given features.
 * @param {object[]} features List of features to check
 */
async function polyfillFeatures(features) {
  for (let index = 0; index < features.length; index++) {
    const feature = features[index];
    logPolyfill(feature.name);
    try {
      await feature.loader();
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }
  return Promise.resolve();
}

/**
 * Gets a list of unsupported features that should be polyfilled.
 * @param {object[]} features List of features to check
 * @return {object[]} List of unsupported features
 */
function getUnsupportedFeatures(features) {
  function isNotSupported(feature) {
    return feature && !feature.isSupported();
  }
  return features.filter(isNotSupported);
}

/**
 * Features required to load the app. The order listed is the order polyfills are loaded.
 */
const requiredFeatures = [
  {
    loader: () => import("core-js/fn/array/includes"),
    isSupported: () => !isUndefined(Array.prototype.includes),
    name: "Array.includes"
  },
  {
    loader: () => import("core-js/fn/object/entries"),
    isSupported: () => !isUndefined(Object.entries),
    name: "Object.entries"
  },
  {
    loader: () => import("core-js/fn/object/values"),
    isSupported: () => !isUndefined(Object.values),
    name: "Object.values"
  },
  {
    loader: () => import("core-js/fn/reflect"),
    isSupported: () => !isUndefined(window.Reflect),
    name: "window.Reflect"
  },
  {
    loader: () => import("@webcomponents/webcomponentsjs"),
    isSupported: () => !isUndefined(window.customElements),
    name: "window.customElements"
  }
];

/**
 * Helper function that loads the required polyfills if they are not supported by the current
 * environment.
 * @return {promise} A promise representing whether the polyfills have been loaded or not
 */
export default async function loadPolyfills() {
  try {
    await polyfillFeatures(getUnsupportedFeatures(requiredFeatures));
    return Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  }
}
